/**
 * ProductBoundary — the admin shell's licence gate for a whole route.
 *
 * Sibling of `PortalBoundary`, and the same shape of backstop: the navigation
 * hides what the tenant cannot use, and this covers everything navigation does
 * not — a typed URL, a bookmark, browser history, a notification deep link.
 *
 * `MainLayout` owns the fetch and publishes the answer through
 * `LicensedProductsProvider`. A React context rather than `Outlet` context
 * because the gated routes sit two Outlets deep (`/` → `organization` →
 * `workers/face-templates`), and forwarding through every intermediate Outlet
 * would put the burden on pages that have nothing to do with licensing.
 *
 * While the licence is unknown this renders a spinner, NOT the page and NOT the
 * redirect. Rendering the page is the flash this exists to remove; redirecting
 * immediately would bounce a legitimately licensed admin off their own bookmark
 * in the ~200ms before the fetch lands.
 *
 * No copy is rendered in any state, so this adds nothing to the nine locale
 * files: the wait is a spinner and the denial is a redirect to a page the tenant
 * can actually use.
 */
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';

import {
  hasProduct,
  UNRESOLVED_LICENSE,
  type ProductCode,
  type ProductLicense,
} from './productAccess';

const LicensedProductsContext = React.createContext<ProductLicense>(UNRESOLVED_LICENSE);

export const LicensedProductsProvider: React.FC<{
  value: ProductLicense;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <LicensedProductsContext.Provider value={value}>{children}</LicensedProductsContext.Provider>
);

/**
 * The current org's licence as far as the shell knows it. A consumer rendered
 * outside the provider gets `UNRESOLVED_LICENSE`, so the default is deny rather
 * than an exception or an accidental allow.
 */
export function useLicensedProducts(): ProductLicense {
  return React.useContext(LicensedProductsContext);
}

/** Convenience for a component that gates one element rather than a route. */
export function useHasProduct(product: ProductCode): boolean {
  return hasProduct(useLicensedProducts(), product);
}

interface ProductBoundaryProps {
  product: ProductCode;
  /** Where an unlicensed tenant lands. The admin shell's home by default. */
  fallback?: string;
  children: React.ReactNode;
}

const AdminProductBoundary: React.FC<ProductBoundaryProps> = ({
  product,
  fallback = '/dashboard',
  children,
}) => {
  const license = useLicensedProducts();

  if (!license.resolved) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, width: '100%' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!hasProduct(license, product)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

export default AdminProductBoundary;
